/**
 * DailyRollup (Ted #15 → #919) — the dashboard's per-service day table.
 *
 * Ted, 09-09: *"I'd like each service to have its own row: Full-Day Daycare,
 * Half-Day Daycare, Boutique Hotel/Boarding, Assessments, Grooming, Hikes,
 * etc. For each category, we should see Expected, Check-Ins, Check-Outs,
 * Overnight/Remaining and Total, along with overall totals … Grooming
 * should be included … Each number should also be clickable — if Full-Day
 * Daycare says 19, tapping 19 should show those 19 dogs … The mobile
 * version should fit the screen properly."* He reads this table several
 * times a day.
 *
 * One source of truth (Principle 1): every number here is the server's fold
 * over the SAME `items` the day list renders (`/rollup`), so a tapped cell
 * links to `/calendar/day` pre-filtered to exactly the dogs it counted —
 * nothing is recounted on the client. The numbers are dogs, not bookings.
 *
 * Design-Philosophy principles served:
 *  - #1 One glance, one truth — the day's shape by service, above the list.
 *  - #2 The next action is obvious — every number is a link, not a dead stat.
 *  - #5 Never lie about data — a structural zero renders "—" with the stale
 *    caption; no ratio set means no invented staffing need. (The A6 floor
 *    meter, with its "(default)" provenance, is the hero's — #922.)
 *  - #6 Tokens only — structural `--ferro-*` for status and chrome; the
 *    service cell wears the owner's `--service-*` color (Ted #17, #921) as a
 *    left rule + tinted name, neutral when no color is chosen.
 *  - #7 Built for hands — cells are ≥44 px tap targets; at 375 px a row
 *    becomes a card with its five numbers on one line, no horizontal scroll.
 */
import { Link } from 'react-router-dom';
import type {
  DashboardRollupResponse,
  DayRollupCounts,
  RollupCell,
} from './dashboard-api.js';
import {
  CELL_LABEL,
  CELL_SHORT_LABEL,
  ROLLUP_CELLS,
  dayListHref,
  viewForCell,
} from './day-list-filters.js';
import { serviceColorClass } from '@ferro/core/modules/tenant-settings/service-colors.js';
import '../../service-colors.css';
import './daily-rollup.css';

interface DailyRollupProps {
  readonly rollup: DashboardRollupResponse | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onRetry?: () => void;
  /** #5 — server says today's zeros may be structural. */
  readonly dataSuspect: boolean;
  /** YYYY-MM-DD the table is showing (drives the links). */
  readonly date: string;
}

function Cell({
  cell,
  counts,
  serviceTypeId,
  date,
  suspect,
}: {
  cell: RollupCell;
  counts: DayRollupCounts;
  serviceTypeId: string | null;
  date: string;
  suspect: boolean;
}): JSX.Element {
  const value = counts[cell];
  // #5 — a structural zero must never read as a confident zero.
  const display = suspect && value === 0 ? '—' : String(value);
  const to = dayListHref({ date, serviceTypeId, view: viewForCell(cell) });
  const cls = [
    'ferro-rollup__cell',
    value === 0 ? 'ferro-rollup__cell--zero' : '',
    suspect ? 'ferro-rollup__cell--stale' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Link
      to={to}
      className={cls}
      role="cell"
      aria-label={`${display} ${CELL_LABEL[cell].toLowerCase()}`}
      data-testid={`rollup-cell-${serviceTypeId ?? 'total'}-${cell}`}
    >
      <span className="ferro-rollup__cell-value">{display}</span>
      <span className="ferro-rollup__cell-label" aria-hidden="true">
        {CELL_SHORT_LABEL[cell]}
      </span>
    </Link>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function DailyRollup({
  rollup,
  isLoading,
  error,
  onRetry,
  dataSuspect,
  date,
}: DailyRollupProps): JSX.Element {
  // Loading (skeleton rows, never a blank block — Principle 4).
  if (!rollup && isLoading) {
    return (
      <section
        className="ferro-rollup ferro-rollup--loading"
        data-testid="daily-rollup-skeleton"
        aria-hidden="true"
      >
        {[0, 1, 2].map((i) => (
          <div key={i} className="ferro-rollup__row ferro-rollup__row--skeleton" />
        ))}
      </section>
    );
  }

  // Error (what happened + how to recover — Principle 2).
  if (!rollup && error) {
    return (
      <section className="ferro-rollup ferro-rollup--error" role="alert" data-testid="daily-rollup-error">
        <span>Couldn’t load the daily rollup.</span>
        {onRetry ? (
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </section>
    );
  }

  const services = rollup?.services ?? [];
  const totals: DayRollupCounts = rollup?.totals ?? {
    expected: 0,
    checkIns: 0,
    checkOuts: 0,
    overnight: 0,
    total: 0,
  };
  const staffing = rollup?.staffing ?? null;

  return (
    <section className="ferro-rollup" aria-label="Daily rollup" data-testid="daily-rollup">
      <header className="ferro-rollup__header">
        <h2 className="ferro-rollup__title">Daily rollup</h2>
        <Link className="ferro-rollup__all-link" to={dayListHref({ date })} data-testid="rollup-full-list">
          Full day list →
        </Link>
      </header>

      {services.length === 0 ? (
        <p className="ferro-rollup__empty" data-testid="daily-rollup-empty">
          {dataSuspect
            ? 'Schedule may be incomplete — see the notice above.'
            : 'Nothing scheduled for this day yet.'}
        </p>
      ) : (
        <div className="ferro-rollup__table" role="table" aria-label="Dogs by service">
          <div className="ferro-rollup__row ferro-rollup__row--head" role="row">
            <span className="ferro-rollup__service" role="columnheader">
              Service
            </span>
            {ROLLUP_CELLS.map((cell) => (
              <span key={cell} className="ferro-rollup__head-cell" role="columnheader">
                {CELL_LABEL[cell]}
              </span>
            ))}
          </div>
          {services.map((row) => (
            <div
              key={row.serviceTypeId}
              className="ferro-rollup__row"
              role="row"
              data-testid={`rollup-row-${row.name}`}
            >
              <span
                className={['ferro-rollup__service', serviceColorClass(row.color)].filter(Boolean).join(' ')}
                role="rowheader"
                data-testid={`rollup-service-${row.name}`}
                data-color={row.color ?? ''}
              >
                {row.displayName}
              </span>
              {ROLLUP_CELLS.map((cell) => (
                <Cell
                  key={cell}
                  cell={cell}
                  counts={row}
                  serviceTypeId={row.serviceTypeId}
                  date={date}
                  suspect={dataSuspect}
                />
              ))}
            </div>
          ))}
          <div className="ferro-rollup__row ferro-rollup__row--totals" role="row" data-testid="rollup-row-totals">
            <span className="ferro-rollup__service" role="rowheader">
              All services
            </span>
            {ROLLUP_CELLS.map((cell) => (
              <Cell
                key={cell}
                cell={cell}
                counts={totals}
                serviceTypeId={null}
                date={date}
                suspect={dataSuspect}
              />
            ))}
          </div>
        </div>
      )}

      {/* Ted A5 — the staffing need sits in the same view as the numbers it
          comes from and never invents a value: no ratio ⇒ a sentence saying
          so. (Ted A6, the floor meter, lives on the hero's snapshot strip
          since #922 — one place, so the two could never disagree.) */}
      {rollup ? (
        <dl className="ferro-rollup__facts" data-testid="rollup-facts">
          <div className="ferro-rollup__fact">
            <dt>Handlers needed</dt>
            <dd data-testid="rollup-staffing">
              {staffing && staffing.dogsPerHandler !== null && staffing.handlersNeededToday !== null ? (
                <>
                  <span className="ferro-rollup__need">
                    {plural(staffing.handlersNeededToday, 'handler', 'handlers')}
                  </span>
                  <span className="ferro-rollup__fact-note">
                    {' '}for {plural(staffing.onFloorPlannedToday, 'dog', 'dogs')} on the floor, at 1 per{' '}
                    {staffing.dogsPerHandler}
                  </span>
                </>
              ) : (
                <span className="ferro-rollup__fact-note">
                  No dogs-per-handler ratio set — add one in Settings → Operations to see the need here.
                </span>
              )}
            </dd>
          </div>
        </dl>
      ) : null}

      {dataSuspect ? (
        <p className="ferro-rollup__caption" data-testid="daily-rollup-stale-caption">
          These numbers may be incomplete — see the notice above.
        </p>
      ) : null}
    </section>
  );
}
